/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { t, Trans } from "@lingui/macro";
import { withI18n } from "@lingui/react";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "beautiful-react-hooks";
import { DropdownInput } from "../../../components/DropdownInput";
import { SpinnerOverlay } from "../../../components/Spinner";
import { Api } from "../../../functions/fetchFromApi";
import { isSSR } from "../../../components/NoSSR";
import Popup from "../../../components/Popup";
import Button from "../../../components/Button";
import { Icon } from "../../../components/Icon";
import { getVoices } from "../../../functions/getVoices";
import useCurrentLanguage from "../../../functions/languages/useCurrentLanguage";
import sendEvent from "../../../functions/analytics";
import * as types from "../../../stores/types";
import { ErrorPopup } from "../ErrorPopup";
import { OnDemandContext } from "../context";
import planet from "../assets/planet.svg";
import Building from "../assets/Building.svg";
import PlacesGrid from "./PlacesGrid";

import "./TourParametersScreen.css";

const HelpPopup = ({ isMobile, theme }) => {
  const commonContent = [
    {
      icon: "timer",
      subtitle: <Trans>Verify opening times before you go</Trans>,
    },
    {
      icon: "not-included",
      subtitle: <Trans>Tickets, entry, and other fees are not included</Trans>,
    },
  ];

  const extraContent = {
    manual: [
      {
        icon: "shopping-cart-gray",
        subtitle: <Trans>The number of points doesn&apos;t impact the final cost</Trans>,
      },
    ],
    mix: [
      {
        icon: "shopping-cart-gray",
        subtitle: <Trans>The number of points doesn&apos;t impact the final cost</Trans>,
      },
    ],
    auto: [],
  };

  const [open, setOpen] = useState(false);

  const handleClosePopup = () => {
    setOpen(false);
    // sendEvent("track", "on_demand_create_click");
    // sendFacebookEvent("track", "on_demand_create_click");
  };

  return (
    <Popup
      hideHeader={!isMobile}
      open={open}
      theme={isMobile ? "bottomSheet" : ""}
      triggerElement={<button className="TourParametersScreen__infoButton">i</button>}
      onOpened={() => setOpen(true)}
      onClosed={handleClosePopup}
    >
      <div className="TourParametersScreen__popup">
        <img src={Building} />
        <div className="TourParametersScreen__popup_text">
          <div className="TourParametersScreen__popup_title">
            <Trans>Additional Info</Trans>
          </div>
          {theme === "manual" && (
            <div className="TourParametersScreen__popup_info">
              <Trans>To create the perfect tour, select between 5 and 15 points.</Trans>
            </div>
          )}
          <div className="TourParametersScreen__popup_subtitles">
            {[...(extraContent[theme] || []), ...commonContent].map((item, index) => (
              <div key={index} className="TourParametersScreen__popup_subtitle">
                <Icon name={item.icon} />
                {item.subtitle}
              </div>
            ))}
          </div>
        </div>
        {!isMobile && (
          <Button theme="black" size="L" onClick={handleClosePopup}>
            <Trans>Ok</Trans>
          </Button>
        )}
      </div>
    </Popup>
  );
};

export default withI18n()(function TourParametersScreen({
  onClickPrev,
  selectedPlaces,
  setSelectedPlaces,
  i18n,
}) {
  const dispatch = useDispatch();
  const isMobile = !isSSR && useMediaQuery("(max-width: 767px)");
  const langPage = useCurrentLanguage();
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedVoice, setSelectedVoice] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("manual");
  const [voices, setVoices] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const {
    mode,
    startingPoint,
    endingPoint,
    areaRadius,
    changeSelectedLanguage,
    changeSelectedVoice,
    changeSelectedMethod,
    changeSelectedPlaces,
  } = useContext(OnDemandContext);
  const [places, setPlaces] = useState(null);
  const [noRoute, setNoRoute] = useState(false);

  useEffect(() => {
    const query = new URLSearchParams();

    query.append("startingPoint", `${startingPoint.lat},${startingPoint.lng}`);
    if (mode === "radius") query.append("radius", areaRadius);
    if (mode === "path") query.append("endingPoint", `${endingPoint.lat},${endingPoint.lng}`);

    Api.get(`/api/quests/aitours/routeSearch?${query.toString()}`)
      .then(response => {
        if (response.detail === "There were not enough places.") {
          setNoRoute(true);
          setPlaces([]);
          sendEvent("track", "not_enough_places_found");
        } else {
          const placesById = new Map(response.results.map(place => [place.place_id, place]));
          setNoRoute(false);
          setPlaces(Array.from(placesById.values()));
        }
      })
      .catch(error => {
        console.error("error", error);
        setNoRoute(true);
        sendEvent("track", "not_enough_places_found");
      });
  }, [mode, startingPoint, endingPoint, areaRadius]);

  useEffect(() => {
    async function fetchVoices() {
      const data = await getVoices();
      setVoices(data);
    }

    fetchVoices();
  }, []);

  const handleToggleDropdown = id => {
    setActiveDropdownId(prevId => (prevId === id ? null : id));
  };

  const voicesForSelectedLanguage = useMemo(
    () => voices.find(lang => lang.code === selectedLanguage)?.voices || [],
    [selectedLanguage, voices],
  );

  const languageOptions = useMemo(
    () =>
      voices.map(lang => ({
        label: lang.name,
        value: lang.code,
      })),
    [voices],
  );

  const voiceOptions = useMemo(
    () =>
      voicesForSelectedLanguage.map(voice => ({
        label: voice.name,
        value: voice.id,
        sample: voice.sample,
      })),
    [voicesForSelectedLanguage],
  );

  const methodOptions = [
    { label: i18n._(t`Select places manually`), value: "manual" },
    { label: i18n._(t`Auto-selected places`), value: "auto" },
    { label: i18n._(t`Include favorite places`), value: "mix" },
  ];

  /* Setting the default language */
  useEffect(() => {
    const hasOptionMatchingPageLang = languageOptions.some(option => option.value === langPage);
    setSelectedLanguage(hasOptionMatchingPageLang ? langPage : "en");
  }, [languageOptions, langPage]);

  /* Setting the default voice */
  useEffect(() => {
    if (voiceOptions.length) {
      setSelectedVoice(voiceOptions[0].value);
    }
  }, [voiceOptions]);

  /* Updating the language in the context */
  useEffect(() => {
    changeSelectedLanguage(selectedLanguage);
  }, [changeSelectedLanguage, selectedLanguage]);

  /* Updating the method in the context */
  useEffect(() => {
    changeSelectedVoice(
      voicesForSelectedLanguage.find(voice => voice.id === selectedVoice) || null,
    );
  }, [changeSelectedVoice, selectedVoice, voicesForSelectedLanguage]);

  useEffect(() => {
    changeSelectedMethod(selectedMethod);
  }, [changeSelectedMethod, selectedMethod]);

  const handleLanguageSelect = selectedOption => {
    setSelectedLanguage(selectedOption.value);
    sendEvent("track", "tour_params_selected_language");
  };

  const handleVoiceSelect = selectedOption => {
    setSelectedVoice(selectedOption.value);
    sendEvent("track", "tour_params_selected_voice");
  };

  const handleMethodSelect = selectedOption => {
    setSelectedMethod(selectedOption.value);
    setSelectedPlaces([]);
    setSelectedImages([]);
  };
  if (!places && !noRoute) return <SpinnerOverlay />;

  if (noRoute)
    return (
      <div>
        <ErrorPopup onClickPrev={onClickPrev} />
      </div>
    );

  const errorAlert = () => {
    dispatch({
      type: types.SHOW_ALERT,
      data: {
        message: <Trans>You can select no more than 15 points!</Trans>,
        actions: [],
      },
    });
  };

  return (
    <div className="TourParametersScreen">
      {typeof window === "object" && (
        <>
          <div className="TourParametersScreen__title">
            <Trans>Tour parameters</Trans>
          </div>
          <span className="TourParametersScreen__helper-text">
            <Trans>Choose your preferred settings for your walking audio tour</Trans>
          </span>
          <div className="TourParametersScreen__input-block">
            <DropdownInput
              closeButton
              search={!isMobile}
              id="language"
              label={<Trans>Language</Trans>}
              icon="globe-language"
              value={selectedLanguage}
              options={languageOptions}
              activeDropdownId={activeDropdownId}
              onSelect={handleLanguageSelect}
              onToggleDropdown={handleToggleDropdown}
            />
            <DropdownInput
              closeButton
              enablePlayer
              id="voice"
              label={<Trans>Voice</Trans>}
              icon="audio-ticket"
              value={selectedVoice}
              options={voiceOptions}
              activeDropdownId={activeDropdownId}
              disabledOption={!selectedLanguage}
              onSelect={handleVoiceSelect}
              onToggleDropdown={handleToggleDropdown}
            />
            <DropdownInput
              closeButton
              id="planning-method"
              label={<Trans>Planning method</Trans>}
              icon="location/white"
              value={selectedMethod}
              options={methodOptions}
              activeDropdownId={activeDropdownId}
              disabledOption={!selectedLanguage}
              onSelect={handleMethodSelect}
              onToggleDropdown={handleToggleDropdown}
            />
          </div>
          {selectedMethod === "auto" ? (
            <div className="TourParametersScreen__auto-selected">
              <img src={planet} className="TourParametersScreen__auto-selected_image" />
              <div>
                <div className="TourParametersScreen__auto-selected_title">
                  <Trans>Let us build your journey</Trans>
                </div>
                <div className="TourParametersScreen__auto-selected_subtitle">
                  <Trans>
                    We’ll find the most exciting and convenient route for you to explore
                  </Trans>
                </div>
              </div>
              <HelpPopup isMobile={isMobile} theme="auto" />
            </div>
          ) : (
            <>
              <div className="TourParametersScreen__helper-text_block">
                <div className="TourParametersScreen__helper-text">
                  <div className="TourParametersScreen__helper-text_title">
                    {selectedMethod === "manual" ? (
                      <Trans>Select the places you want to visit: {selectedPlaces.length}/15</Trans>
                    ) : (
                      <Trans>
                        Include the places you want to visit: {selectedPlaces.length}/15
                      </Trans>
                    )}
                  </div>
                  <div className="TourParametersScreen__helper-text_description">
                    {selectedMethod === "manual" ? (
                      <Trans>To create the perfect tour, select between 5 and 15 points.</Trans>
                    ) : (
                      <Trans>
                        Add your places - if fewer than 15, we’ll add more to complete your tour
                      </Trans>
                    )}
                  </div>
                </div>
                <HelpPopup isMobile={isMobile} theme={selectedMethod} />
              </div>
              <PlacesGrid
                places={places}
                errorAlert={errorAlert}
                setSelectedImages={setSelectedImages}
                selectedImages={selectedImages}
                onSelect={changeSelectedPlaces}
              />
            </>
          )}
        </>
      )}
    </div>
  );
});
